import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, X, DollarSign, Layers, Tag, Boxes, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import api from '../services/api';
import PreciosPorProveedor from '../components/PreciosPorProveedor';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import AutocompleteGrupo from '../components/AutocompleteGrupo';
import Paginacion from '../components/Paginacion';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

const PAGE_SIZE = 50;

function fechaHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const TIPOS = [
  { value: 'materia_prima', label: 'Materia prima' },
  { value: 'intermedio', label: 'Producto intermedio' },
  { value: 'terminado', label: 'Producto terminado' },
  { value: 'servicio', label: 'Servicio' }
];
const ORDEN_TIPOS = TIPOS.map((t) => t.value);
const UNIDADES = ['Kg', 'Lt', 'Unidad'];
// Presentaciones en las que se puede cargar el costo de reposición; se
// guarda dividido por `contenido` (unidades del producto que trae).
const PRESENTACIONES = [
  { value: 'docena', label: 'Docena', contenido: 12 },
  { value: 'ciento', label: 'Ciento', contenido: 100 },
  { value: 'millar', label: 'Millar', contenido: 1000 },
  { value: 'caja', label: 'Caja' },
  { value: 'bulto', label: 'Bulto' },
  { value: 'paquete', label: 'Paquete' },
  { value: 'rollo', label: 'Rollo' }
];
const COSTO_VACIO = { fecha: '', costo_unitario: '', moneda: 'USD', presentacion: 'unidad', contenido: '' };

const FORM_VACIO = { nombre: '', tipo: 'materia_prima', unidad_medida: 'Unidad', tallas_disponibles: '', disponibilidad: 'compra' };

export default function Productos({ embebido = false } = {}) {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;
  const navigate = useNavigate();

  // Los paneles de estructura de costo y de precios en Productos son de solo
  // lectura: la edición vive en la pantalla "Estructura de costo". Este atajo
  // deja preseleccionado el producto y la pestaña vía localStorage (misma clave
  // que lee EstructuraCosto.jsx al montar).
  function irAEstructura(productoId, tab) {
    try {
      localStorage.setItem(`estructura-costo:${empresaActiva}`, JSON.stringify({ selId: productoId, tab }));
    } catch { /* ignore */ }
    navigate('/inventario/estructura-costo');
  }

  const [productos, setProductos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  // ?q= llega desde el buscador general con el nombre del producto.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setBusqueda(q);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [ventaFiltro, setVentaFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [gruposExpandidos, setGruposExpandidos] = useState(new Set());
  const [grupoAbiertoId, setGrupoAbiertoId] = useState(null);
  const [errorGrupo, setErrorGrupo] = useState('');
  const [grupoEnEdicionId, setGrupoEnEdicionId] = useState(null);
  const [formEditarGrupo, setFormEditarGrupo] = useState({ nombre: '' });
  const [errorEditarGrupo, setErrorEditarGrupo] = useState('');
  const [categoriaAbiertaId, setCategoriaAbiertaId] = useState(null);
  const [errorCategoriaGrupo, setErrorCategoriaGrupo] = useState('');

  const [costoAbiertoId, setCostoAbiertoId] = useState(null);
  const [costoVigente, setCostoVigente] = useState(null);
  const [costoActual, setCostoActual] = useState(null);
  const [tasaCosto, setTasaCosto] = useState('BCV');
  const [formCosto, setFormCosto] = useState({ ...COSTO_VACIO, fecha: fechaHoy() });
  const [errorCosto, setErrorCosto] = useState('');
  const [costoFormAbierto, setCostoFormAbierto] = useState(false);

  const [recetaAbiertaId, setRecetaAbiertaId] = useState(null);
  const [recetaLineas, setRecetaLineas] = useState([]);
  const [costoVivo, setCostoVivo] = useState(null);
  const [tasaReceta, setTasaReceta] = useState('BCV');

  const [precioAbiertoId, setPrecioAbiertoId] = useState(null);
  const [precios, setPrecios] = useState([]);
  const [rentabilidades, setRentabilidades] = useState({});

  async function cargar() {
    const [prod, grup] = await Promise.all([
      api.get('/productos'),
      api.get('/productos-grupo')
    ]);
    setProductos(prod.data);
    setGrupos(grup.data);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  useEffect(() => {
    setPagina(1);
  }, [busqueda, tipoFiltro]);

  function empezarEdicion(p) {
    setEditandoId(p.id);
    setForm({
      nombre: p.nombre,
      tipo: p.tipo,
      unidad_medida: p.unidad_medida,
      tallas_disponibles: p.tallas_disponibles || '',
      disponibilidad: p.disponibilidad
    });
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    try {
      const payload = { ...form };
      if (editandoId) {
        await api.put(`/productos/${editandoId}`, payload);
      } else {
        await api.post('/productos', payload);
      }
      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el producto');
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await api.delete(`/productos/${id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el producto');
    }
  }

  function alternarGrupoPanel(id) {
    setGrupoAbiertoId(grupoAbiertoId === id ? null : id);
    setErrorGrupo('');
  }

  async function asignarGrupo(producto, grupoId) {
    setErrorGrupo('');
    try {
      await api.put(`/productos/${producto.id}`, { grupo_id: grupoId });
      setGruposExpandidos((prev) => new Set(prev).add(grupoId));
      setGrupoAbiertoId(null);
      cargar();
    } catch (err) {
      setErrorGrupo(err.response?.data?.error || 'No se pudo asignar el grupo');
    }
  }

  async function crearYAsignarGrupo(producto, nombre) {
    setErrorGrupo('');
    if (!nombre) return;
    try {
      const { data } = await api.post('/productos-grupo', { nombre });
      await asignarGrupo(producto, data.id);
    } catch (err) {
      setErrorGrupo(err.response?.data?.error || 'No se pudo crear el grupo');
    }
  }

  async function quitarDeGrupo(producto) {
    setErrorGrupo('');
    try {
      await api.put(`/productos/${producto.id}`, { grupo_id: null });
      setGrupoAbiertoId(null);
      cargar();
    } catch (err) {
      setErrorGrupo(err.response?.data?.error || 'No se pudo quitar el producto del grupo');
    }
  }

  function alternarExpandirGrupo(grupoId) {
    setGruposExpandidos((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(grupoId)) nuevo.delete(grupoId); else nuevo.add(grupoId);
      return nuevo;
    });
  }

  function abrirEdicionGrupo(grupo, e) {
    e.stopPropagation();
    setGrupoEnEdicionId(grupoEnEdicionId === grupo.id ? null : grupo.id);
    setFormEditarGrupo({ nombre: grupo.nombre });
    setErrorEditarGrupo('');
  }

  async function guardarEdicionGrupo(grupoId) {
    setErrorEditarGrupo('');
    try {
      await api.put(`/productos-grupo/${grupoId}`, formEditarGrupo);
      setGrupoEnEdicionId(null);
      cargar();
    } catch (err) {
      setErrorEditarGrupo(err.response?.data?.error || 'No se pudo guardar el grupo');
    }
  }

  function alternarCategoriaPanel(grupoId, e) {
    e.stopPropagation();
    setCategoriaAbiertaId(categoriaAbiertaId === grupoId ? null : grupoId);
    setErrorCategoriaGrupo('');
  }

  async function asignarCategoria(grupoId, categoriaId) {
    setErrorCategoriaGrupo('');
    try {
      await api.put(`/productos-grupo/${grupoId}`, { padre_id: categoriaId });
      setGruposExpandidos((prev) => new Set(prev).add(categoriaId).add(grupoId));
      setCategoriaAbiertaId(null);
      cargar();
    } catch (err) {
      setErrorCategoriaGrupo(err.response?.data?.error || 'No se pudo asignar la categoría');
    }
  }

  async function crearYAsignarCategoria(grupoId, nombre) {
    setErrorCategoriaGrupo('');
    if (!nombre) return;
    try {
      const { data } = await api.post('/productos-grupo', { nombre });
      await asignarCategoria(grupoId, data.id);
    } catch (err) {
      setErrorCategoriaGrupo(err.response?.data?.error || 'No se pudo crear la categoría');
    }
  }

  async function quitarCategoria(grupoId) {
    setErrorCategoriaGrupo('');
    try {
      await api.put(`/productos-grupo/${grupoId}`, { padre_id: null });
      setCategoriaAbiertaId(null);
      cargar();
    } catch (err) {
      setErrorCategoriaGrupo(err.response?.data?.error || 'No se pudo quitar la categoría');
    }
  }

  async function eliminarGrupo(grupo, e) {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar el grupo "${grupo.nombre}"? Sus presentaciones y subgrupos quedan sueltos, sin agrupar.`)) return;
    try {
      await api.delete(`/productos-grupo/${grupo.id}`);
      setGrupoEnEdicionId(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el grupo');
    }
  }

  const tipoLabel = (t) => TIPOS.find((x) => x.value === t)?.label || t;

  // Filtro por nombre (propio, del grupo o de la categoría) + tipo +
  // venta/compra. Si coincide el nombre del grupo, el de la categoría o
  // el de alguna presentación, el grupo entero se mantiene visible (no
  // se filtran las presentaciones dentro de un grupo que ya matchea).
  const q = busqueda.trim().toLowerCase();
  const gruposPorId = new Map(grupos.map((g) => [g.id, g]));
  const grupoIdsConCoincidencia = new Set();
  if (q) {
    // Un grupo coincide si su nombre —o el de cualquiera de sus
    // ancestros— contiene el texto buscado.
    grupos.forEach((g) => {
      let actual = g;
      const vistos = new Set();
      while (actual && !vistos.has(actual.id)) {
        vistos.add(actual.id);
        if (actual.nombre.toLowerCase().includes(q)) { grupoIdsConCoincidencia.add(g.id); break; }
        actual = actual.padre_id ? gruposPorId.get(actual.padre_id) : null;
      }
    });
    productos.forEach((p) => {
      if (p.nombre.toLowerCase().includes(q) && p.grupo_id) grupoIdsConCoincidencia.add(p.grupo_id);
    });
  }
  const productosFiltrados = productos.filter((p) => {
    if (!p.activo) return false;
    if (tipoFiltro && p.tipo !== tipoFiltro) return false;
    if (ventaFiltro && p.disponibilidad !== ventaFiltro) return false;
    if (!q) return true;
    if (p.nombre.toLowerCase().includes(q)) return true;
    if (p.grupo_id && grupoIdsConCoincidencia.has(p.grupo_id)) return true;
    return false;
  });

  // Árbol de grupos de profundidad libre. Cada nodo de grupo:
  //   { esGrupo:true, grupo_id, grupo_nombre, tipo, presentaciones:[], hijos:[], hijosVisibles:[] }
  // Los productos sin grupo se juntan, por tipo, en un grupo visual "Sin grupo"
  // ({ virtual:true }) que va al final del tipo y no se puede editar ni borrar.
  // Un grupo se muestra solo si él o alguno de sus descendientes tiene
  // presentaciones que pasan el filtro (igual que antes: un grupo vacío
  // no ocupa lugar). El `tipo` y el orden se resuelven de abajo hacia
  // arriba; tipo en orden de proceso (materia prima, intermedio,
  // terminado) primero, y dentro del mismo tipo, alfabético.
  const entradasNivelSuperior = (() => {
    const presPorGrupo = new Map();
    const sinGrupoPorTipo = new Map();
    productosFiltrados.forEach((p) => {
      if (p.grupo_id) {
        if (!presPorGrupo.has(p.grupo_id)) presPorGrupo.set(p.grupo_id, []);
        presPorGrupo.get(p.grupo_id).push(p);
      } else {
        if (!sinGrupoPorTipo.has(p.tipo)) sinGrupoPorTipo.set(p.tipo, []);
        sinGrupoPorTipo.get(p.tipo).push(p);
      }
    });
    presPorGrupo.forEach((arr) => arr.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
    const sinGrupo = [...sinGrupoPorTipo].map(([tipo, presentaciones]) => ({
      esGrupo: true,
      virtual: true,
      grupo_id: `sin-grupo-${tipo}`,
      grupo_nombre: 'Sin grupo',
      tipo,
      presentaciones: presentaciones.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
      hijos: [],
      hijosVisibles: []
    }));

    const nodoPorId = new Map();
    grupos.forEach((g) => {
      nodoPorId.set(g.id, {
        esGrupo: true,
        grupo_id: g.id,
        grupo_nombre: g.nombre,
        padre_id: g.padre_id || null,
        presentaciones: presPorGrupo.get(g.id) || [],
        hijos: [],
        hijosVisibles: []
      });
    });

    const raices = [];
    nodoPorId.forEach((nodo) => {
      const padre = nodo.padre_id ? nodoPorId.get(nodo.padre_id) : null;
      if (padre) padre.hijos.push(nodo);
      else raices.push(nodo);
    });

    const resolver = (nodo) => {
      nodo.hijos.forEach(resolver);
      nodo.hijos.sort((a, b) => a.grupo_nombre.localeCompare(b.grupo_nombre, 'es'));
      nodo.hijosVisibles = nodo.hijos.filter((h) => h.visible);
      nodo.visible = nodo.presentaciones.length > 0 || nodo.hijosVisibles.length > 0;
      nodo.tipo = nodo.hijosVisibles[0]?.tipo || nodo.presentaciones[0]?.tipo || ORDEN_TIPOS[0];
    };
    raices.forEach(resolver);

    return [...raices.filter((n) => n.visible), ...sinGrupo].sort((a, b) => {
      if (a.tipo !== b.tipo) return ORDEN_TIPOS.indexOf(a.tipo) - ORDEN_TIPOS.indexOf(b.tipo);
      if (!!a.virtual !== !!b.virtual) return a.virtual ? 1 : -1;
      return a.grupo_nombre.localeCompare(b.grupo_nombre, 'es');
    });
  })();

  const totalPaginas = Math.max(1, Math.ceil(entradasNivelSuperior.length / PAGE_SIZE));
  const entradasPaginaActual = entradasNivelSuperior.slice((pagina - 1) * PAGE_SIZE, pagina * PAGE_SIZE);

  // Entradas de la página actual, agrupadas por tipo para el encabezado de sección.
  const seccionesPorTipo = (() => {
    const secciones = {};
    entradasPaginaActual.forEach((e) => {
      const clave = tipoLabel(e.tipo);
      if (!secciones[clave]) secciones[clave] = [];
      secciones[clave].push(e);
    });
    return Object.keys(secciones).map((tipo) => ({ tipo, entradas: secciones[tipo] }));
  })();

  async function cargarCosto(productoId, tasa) {
    const [vigente, actual] = await Promise.all([
      api.get('/producto-costo-historial/vigente', { params: { producto_id: productoId } }),
      api.get('/producto-receta/costo-vivo', { params: { producto_id: productoId, tasa: tasa || tasaCosto } })
    ]);
    setCostoVigente(vigente.data);
    setCostoActual(actual.data);
  }

  async function alternarCosto(id) {
    if (costoAbiertoId === id) {
      setCostoAbiertoId(null);
      return;
    }
    setCostoAbiertoId(id);
    setErrorCosto('');
    setFormCosto({ ...COSTO_VACIO, fecha: fechaHoy() });
    setCostoFormAbierto(false);
    setTasaCosto('BCV');
    setCostoVigente('cargando');
    try {
      await cargarCosto(id, 'BCV');
    } catch (err) {
      setCostoVigente(null);
      setCostoActual(null);
    }
  }

  async function cambiarTasaCosto(tasa, productoId) {
    setTasaCosto(tasa);
    try {
      await cargarCosto(productoId, tasa);
    } catch (err) {
      setCostoActual(null);
    }
  }

  async function guardarCosto(e, productoId) {
    e.preventDefault();
    setErrorCosto('');
    if (!formCosto.costo_unitario) return;
    const porUnidad = formCosto.presentacion === 'unidad';
    if (!porUnidad && !(Number(formCosto.contenido) > 0)) {
      setErrorCosto('Indicá cuántas unidades trae la presentación');
      return;
    }
    try {
      await api.post('/producto-costo-historial', {
        producto_id: productoId,
        fecha: formCosto.fecha,
        costo_unitario: formCosto.costo_unitario,
        moneda: formCosto.moneda,
        presentacion: porUnidad ? null : formCosto.presentacion,
        contenido: porUnidad ? 1 : formCosto.contenido,
        origen: 'manual'
      });
      await cargarCosto(productoId);
      setFormCosto({ ...COSTO_VACIO, fecha: fechaHoy() });
      setCostoFormAbierto(false);
    } catch (err) {
      setErrorCosto(err.response?.data?.error || 'No se pudo registrar el costo');
    }
  }

  function cambiarPresentacion(valor) {
    const pres = PRESENTACIONES.find((x) => x.value === valor);
    setFormCosto((f) => ({ ...f, presentacion: valor, contenido: pres?.contenido ? String(pres.contenido) : '' }));
  }

  async function cargarReceta(productoId, tasa) {
    const [lineas, vivo] = await Promise.all([
      api.get('/producto-receta', { params: { producto_id: productoId } }),
      api.get('/producto-receta/costo-vivo', { params: { producto_id: productoId, tasa: tasa || tasaReceta } })
    ]);
    setRecetaLineas(lineas.data);
    setCostoVivo(vivo.data);
  }

  async function cambiarTasaReceta(tasa, productoId) {
    setTasaReceta(tasa);
    setCostoVivo('cargando');
    try {
      await cargarReceta(productoId, tasa);
    } catch (err) {
      setCostoVivo(null);
    }
  }

  async function alternarReceta(id) {
    if (recetaAbiertaId === id) {
      setRecetaAbiertaId(null);
      return;
    }
    setRecetaAbiertaId(id);
    setRecetaLineas([]);
    setTasaReceta('BCV');
    setCostoVivo('cargando');
    try {
      await cargarReceta(id, 'BCV');
    } catch (err) {
      setCostoVivo(null);
    }
  }

  async function cargarPrecios(producto) {
    const { data } = await api.get('/producto-precio', { params: { producto_id: producto.id, vigentes: 1 } });
    setPrecios(data);

    const combos = [...new Set(data.map((p) => `${p.tipo_precio}|${p.tipo_tasa}`))];
    const resultados = await Promise.all(combos.map(async (combo) => {
      const [tipo_precio, tipo_tasa] = combo.split('|');
      const { data: rent } = await api.get('/producto-precio/rentabilidad', { params: { producto_id: producto.id, tipo_precio, tipo_tasa } });
      return [combo, rent];
    }));
    setRentabilidades(Object.fromEntries(resultados));
  }

  async function alternarPrecio(producto) {
    if (precioAbiertoId === producto.id) {
      setPrecioAbiertoId(null);
      return;
    }
    setPrecioAbiertoId(producto.id);
    setPrecios('cargando');
    setRentabilidades({});
    try {
      await cargarPrecios(producto);
    } catch (err) {
      setPrecios([]);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {embebido ? <span /> : (
          <h1 className="text-2xl font-bold text-slate-800">
            Productos
            {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
            <BotonAyuda />
          </h1>
        )}
        <button
          onClick={() => (mostrarForm ? cancelar() : setMostrarForm(true))}
          title={mostrarForm ? 'Cancelar' : 'Nuevo producto'}
          className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md self-start sm:self-auto"
        >
          {mostrarForm ? <X size={18} /> : <Plus size={18} />}
        </button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          placeholder="Buscar producto por nombre..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="border rounded-md px-3 py-2 w-full sm:max-w-md"
        />
        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          className="border rounded-md px-3 py-2 sm:max-w-xs"
        >
          <option value="">Todos los tipos</option>
          {TIPOS.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <select
          value={ventaFiltro}
          onChange={(e) => setVentaFiltro(e.target.value)}
          className="border rounded-md px-3 py-2 sm:max-w-xs"
        >
          <option value="">Todos</option>
          <option value="compra">Solo compra (insumo)</option>
          <option value="venta">Solo venta</option>
          <option value="ambas">Compra y Venta</option>
          <option value="contable">Contable / administrativo</option>
        </select>
      </div>

      {mostrarForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium text-slate-700">
                {editandoId ? 'Editando producto' : 'Nuevo producto'}
              </h2>
              <button onClick={cancelar} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                required
                autoFocus
                placeholder="Nombre del producto"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              />

              <select
                value={form.tipo}
                onChange={(e) => {
                  const tipo = e.target.value;
                  // Un servicio nunca es inventario: se asume contable.
                  setForm((f) => ({
                    ...f,
                    tipo,
                    disponibilidad: tipo === 'servicio' ? 'contable' : f.disponibilidad
                  }));
                }}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              >
                {TIPOS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>

              <select
                value={form.unidad_medida}
                onChange={(e) => setForm({ ...form, unidad_medida: e.target.value })}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              >
                {UNIDADES.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>

              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Tallas disponibles (opcional)</label>
                <input
                  placeholder="Ej: S, M, L, XL"
                  value={form.tallas_disponibles || ''}
                  onChange={(e) => setForm({ ...form, tallas_disponibles: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Disponibilidad</label>
                <select
                  value={form.disponibilidad}
                  onChange={(e) => setForm({ ...form, disponibilidad: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                >
                  <option value="compra">Solo compra (insumo)</option>
                  <option value="venta">Solo venta</option>
                  <option value="ambas">Compra y Venta</option>
                  <option value="contable">Contable / administrativo</option>
                </select>
              </div>

              {error && <p className="text-red-600 text-sm sm:col-span-2">{error}</p>}

              <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-2 hover:bg-primary-700">
                {editandoId ? 'Guardar cambios' : 'Guardar'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="divide-y divide-slate-100">
          {seccionesPorTipo.map((seccion) => (
            <div key={seccion.tipo}>
              <div className="px-3 py-1.5 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-400">
                {seccion.tipo}
              </div>
              <div className="p-3 space-y-3">
                {renderSeccionEntradas(seccion.entradas)}
              </div>
            </div>
          ))}
          {entradasNivelSuperior.length === 0 && (
            <div className="p-6 text-center text-slate-400 text-sm">
              {busqueda ? 'Ningún producto coincide con la búsqueda.' : 'No hay productos creados todavía.'}
            </div>
          )}
        </div>
        <Paginacion pagina={pagina} totalPaginas={totalPaginas} total={entradasNivelSuperior.length} onCambiarPagina={setPagina} />
      </div>
    </div>
  );

  // Alterna tarjetas de producto sueltas (agrupadas en una grilla) con
  // cabeceras de grupo (fuera de la grilla) preservando el orden ya resuelto.
  function renderSeccionEntradas(entradas) {
    const bloques = [];
    let bufferSueltos = [];
    const flushSueltos = () => {
      if (bufferSueltos.length > 0) {
        bloques.push(
          <div key={`sueltos-${bloques.length}`} className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
            {bufferSueltos.map((p) => (
              <React.Fragment key={p.id}>
                {renderTarjetaProducto(p)}
                {renderPanelesProducto(p)}
              </React.Fragment>
            ))}
          </div>
        );
        bufferSueltos = [];
      }
    };
    entradas.forEach((entrada) => {
      if (entrada.esGrupo) {
        flushSueltos();
        bloques.push(renderCabeceraGrupo(entrada, { profundidad: 0 }));
      } else {
        bufferSueltos.push(entrada.producto);
      }
    });
    flushSueltos();
    return bloques;
  }

  function renderTarjetaProducto(p) {
    return (
      <div className="border border-slate-200 rounded-lg p-3 flex flex-col gap-2 bg-white">
        <div>
          <p className="text-sm font-medium text-slate-800 break-words">{p.nombre}</p>
          {p.tallas_disponibles && (
            <p className="text-xs text-slate-500 mt-0.5">Tallas: {p.tallas_disponibles}</p>
          )}
          <p className="text-xs text-slate-400 mt-0.5">
            {p.unidad_medida}
            {p.disponibilidad === 'compra' && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 align-middle">Solo compra</span>
            )}
            {p.disponibilidad === 'ambas' && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 align-middle">Compra y Venta</span>
            )}
            {p.disponibilidad === 'contable' && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 align-middle">Contable</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2.5 mt-auto pt-1.5 border-t border-slate-100">
          <button
            onClick={() => alternarCosto(p.id)}
            title="Costo"
            className={`transition-colors ${costoAbiertoId === p.id ? 'text-emerald-600' : 'text-slate-400 hover:text-emerald-600'}`}
          >
            <DollarSign size={15} />
          </button>
          <button
            onClick={() => alternarReceta(p.id)}
            title="Estructura de costo"
            className={`transition-colors ${recetaAbiertaId === p.id ? 'text-primary-600' : 'text-slate-400 hover:text-primary-600'}`}
          >
            <Layers size={15} />
          </button>
          <button
            onClick={() => alternarPrecio(p)}
            title="Precios y rentabilidad"
            className={`transition-colors ${precioAbiertoId === p.id ? 'text-amber-600' : 'text-slate-400 hover:text-amber-600'}`}
          >
            <Tag size={15} />
          </button>
          <button
            onClick={() => alternarGrupoPanel(p.id)}
            title="Agrupar con otras presentaciones"
            className={`transition-colors ${grupoAbiertoId === p.id ? 'text-indigo-600' : 'text-slate-400 hover:text-indigo-600'}`}
          >
            <Boxes size={15} />
          </button>
          <button
            onClick={() => empezarEdicion(p)}
            title="Editar"
            className="text-slate-400 hover:text-primary-600 transition-colors"
          >
            <Pencil size={15} />
          </button>
          <button
            onClick={() => eliminar(p.id)}
            title="Eliminar"
            className="text-slate-400 hover:text-red-600 transition-colors"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    );
  }

  // Paneles expandibles (costo, estructura de costo, precios, agrupar) de una
  // tarjeta de producto. `gridColumn: '1 / -1'` los hace ocupar todo el ancho
  // de la grilla en la fila siguiente a la tarjeta, sin romper el layout.
  function renderPanelesProducto(p) {
    return (
      <>
        {costoAbiertoId === p.id && (
                    <div className="bg-slate-50 rounded-lg p-3" style={{ gridColumn: '1 / -1' }}>
                        {costoVigente === 'cargando' ? (
                          <p className="text-sm text-slate-400">Cargando…</p>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                              {costoVigente ? (
                                <>
                                  <span className="text-slate-500">Costo vigente</span>
                                  <span className="font-semibold text-slate-800 tabular-nums">
                                    {costoActual && costoActual.costo_unitario !== null ? `$${formatMonto(costoActual.costo_unitario, 6)}` : 'sin tasa del día'}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {formatFecha(costoVigente.fecha)}
                                    {' · '}{costoVigente.origen === 'factura' ? 'factura' : 'manual'}
                                    {costoVigente.proveedor_nombre && ` · ${costoVigente.proveedor_nombre}`}
                                    {' · '}{costoVigente.moneda === 'BS' ? 'BS' : 'USD'}
                                  </span>
                                </>
                              ) : (
                                <span className="text-slate-500">Sin costo registrado</span>
                              )}
                              <select
                                value={tasaCosto}
                                onChange={(e) => cambiarTasaCosto(e.target.value, p.id)}
                                className="border rounded-md px-1.5 py-0.5 text-xs"
                              >
                                <option value="BCV">BCV</option>
                                <option value="USD">USD</option>
                              </select>
                              {!costoFormAbierto && (
                                <button
                                  onClick={() => setCostoFormAbierto(true)}
                                  title="Registrar costo"
                                  className="text-slate-400 hover:text-primary-600 transition-colors"
                                >
                                  <Plus size={15} />
                                </button>
                              )}
                            </div>

                            {costoFormAbierto && (
                              <form onSubmit={(e) => guardarCosto(e, p.id)} className="flex flex-wrap items-end gap-2 border border-dashed rounded-lg bg-white p-3">
                                <div>
                                  <label className="block text-xs text-slate-500 mb-1">Fecha</label>
                                  <input
                                    type="date"
                                    value={formCosto.fecha}
                                    onChange={(e) => setFormCosto({ ...formCosto, fecha: e.target.value })}
                                    className="border rounded-md px-2 py-1.5 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-500 mb-1">Costo de reposición</label>
                                  <input
                                    type="number"
                                    step="any"
                                    required
                                    value={formCosto.costo_unitario}
                                    onChange={(e) => setFormCosto({ ...formCosto, costo_unitario: e.target.value })}
                                    className="border rounded-md px-2 py-1.5 text-sm w-32"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-500 mb-1">Moneda</label>
                                  <select
                                    value={formCosto.moneda}
                                    onChange={(e) => setFormCosto({ ...formCosto, moneda: e.target.value })}
                                    className="border rounded-md px-2 py-1.5 text-sm"
                                  >
                                    <option value="USD">USD</option>
                                    <option value="USD_BCV">BCV</option>
                                    <option value="BS">BS</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-slate-500 mb-1">Por</label>
                                  <select
                                    value={formCosto.presentacion}
                                    onChange={(e) => cambiarPresentacion(e.target.value)}
                                    className="border rounded-md px-2 py-1.5 text-sm"
                                  >
                                    <option value="unidad">{p.unidad_medida}</option>
                                    {PRESENTACIONES.map((x) => (
                                      <option key={x.value} value={x.value}>{x.label}</option>
                                    ))}
                                  </select>
                                </div>
                                {formCosto.presentacion !== 'unidad' && (
                                  <div>
                                    <label className="block text-xs text-slate-500 mb-1">Trae ({p.unidad_medida})</label>
                                    <input
                                      type="number"
                                      step="any"
                                      min="0"
                                      required
                                      value={formCosto.contenido}
                                      onChange={(e) => setFormCosto({ ...formCosto, contenido: e.target.value })}
                                      className="border rounded-md px-2 py-1.5 text-sm w-24"
                                    />
                                  </div>
                                )}
                                <button type="submit" className="bg-primary-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-primary-700">
                                  Registrar
                                </button>
                                <button type="button" onClick={() => { setCostoFormAbierto(false); setErrorCosto(''); }} className="text-slate-500 text-sm hover:underline">
                                  Cancelar
                                </button>
                                {errorCosto && <p className="text-red-600 text-xs w-full">{errorCosto}</p>}

                                {formCosto.presentacion !== 'unidad' && Number(formCosto.costo_unitario) > 0 && Number(formCosto.contenido) > 0 && (
                                  <p className="text-xs text-slate-400 w-full">
                                    = {formatMonto(Number(formCosto.costo_unitario) / Number(formCosto.contenido), 6)} {formCosto.moneda === 'USD_BCV' ? 'BCV' : formCosto.moneda} por {p.unidad_medida} (se guarda así)
                                  </p>
                                )}
                              </form>
                            )}

                            <PreciosPorProveedor productoId={p.id} tasa={tasaCosto} unidad={p.unidad_medida} />
                          </div>
                        )}
                    </div>
        )}
        {recetaAbiertaId === p.id && (
                    <div className="bg-slate-50 rounded-lg p-3" style={{ gridColumn: '1 / -1' }}>
                        {costoVivo === 'cargando' ? (
                          <p className="text-sm text-slate-400">Cargando…</p>
                        ) : (
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                            <select
                              value={tasaReceta}
                              onChange={(e) => cambiarTasaReceta(e.target.value, p.id)}
                              className="border rounded-md px-1.5 py-0.5 text-xs"
                            >
                              <option value="BCV">BCV</option>
                              <option value="USD">USD</option>
                            </select>
                            {recetaLineas.length === 0 ? (
                              <span className="text-slate-500">Sin estructura de costo — el costo sale de carga manual o facturas.</span>
                            ) : costoVivo?.costo_unitario != null ? (
                              <>
                                <span className="text-slate-500">Costo en vivo</span>
                                <span className="font-semibold text-emerald-700 tabular-nums">${formatMonto(costoVivo.costo_unitario, 6)}</span>
                                <span className="text-xs text-slate-400">
                                  por {p.unidad_medida} · {recetaLineas.length} línea{recetaLineas.length === 1 ? '' : 's'} · merma {formatMonto(costoVivo.merma_pct)} %
                                </span>
                              </>
                            ) : (
                              <span className="text-xs text-amber-600">Costo en vivo incompleto — falta costo en algún componente.</span>
                            )}
                            <button
                              onClick={() => irAEstructura(p.id, 'estructura')}
                              title="Ver / editar en Estructura de costo"
                              className="text-slate-400 hover:text-primary-600 transition-colors"
                            >
                              <ExternalLink size={15} />
                            </button>
                          </div>
                        )}
                    </div>
        )}
        {precioAbiertoId === p.id && (
                    <div className="bg-slate-50 rounded-lg p-3" style={{ gridColumn: '1 / -1' }}>
                        {precios === 'cargando' ? (
                          <p className="text-sm text-slate-400">Cargando…</p>
                        ) : (
                          <div className="flex items-start gap-2">
                            {precios.length > 0 ? (
                              <table className="flex-1 text-xs">
                                <thead className="text-slate-400 text-left">
                                  <tr>
                                    <th className="py-0.5 pr-3">Tipo</th>
                                    <th className="py-0.5 pr-3">Tasa</th>
                                    <th className="py-0.5 pr-3">Vigente</th>
                                    <th className="py-0.5 pr-3 text-right">Precio</th>
                                    <th className="py-0.5 pr-3 text-right">Costo</th>
                                    <th className="py-0.5 text-right">Margen</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {precios.map((pr) => {
                                    const rent = rentabilidades[`${pr.tipo_precio}|${pr.tipo_tasa}`];
                                    return (
                                      <tr key={pr.id} className="border-t border-slate-200">
                                        <td className="py-1 pr-3 capitalize">{pr.tipo_precio}</td>
                                        <td className="py-1 pr-3">{pr.tipo_tasa}</td>
                                        <td className="py-1 pr-3 tabular-nums">{formatFecha(pr.vigente_desde)}</td>
                                        <td className="py-1 pr-3 text-right tabular-nums">${formatMonto(pr.precio)}</td>
                                        <td className="py-1 pr-3 text-right tabular-nums text-slate-500">{rent?.costo_unitario !== null && rent?.costo_unitario !== undefined ? `$${formatMonto(rent.costo_unitario)}` : '-'}</td>
                                        <td className={`py-1 text-right tabular-nums ${rent?.margen_real >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {rent?.margen_real !== null && rent?.margen_real !== undefined ? `${formatMonto(rent.margen_real)}%` : '-'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            ) : (
                              <span className="flex-1 text-xs text-slate-400">Sin precios cargados.</span>
                            )}
                            <button
                              onClick={() => irAEstructura(p.id, 'precios')}
                              title="Ver / editar en Estructura de costo"
                              className="mt-0.5 text-slate-400 hover:text-primary-600 transition-colors"
                            >
                              <ExternalLink size={15} />
                            </button>
                          </div>
                        )}
                    </div>
        )}
        {grupoAbiertoId === p.id && (
                    <div className="bg-slate-50 rounded-lg p-4" style={{ gridColumn: '1 / -1' }}>
                        <div className="space-y-3">
                          {p.grupo_id ? (
                            <p className="text-sm text-slate-600">
                              Agrupado con <span className="font-medium">{p.grupo_nombre}</span>.{' '}
                              <button onClick={() => quitarDeGrupo(p)} className="text-red-600 hover:underline">Quitar del grupo</button>
                            </p>
                          ) : (
                            <p className="text-sm text-slate-400">Este producto no pertenece a ningún grupo todavía.</p>
                          )}
                          <div className="w-64">
                            <label className="block text-xs text-slate-500 mb-1">{p.grupo_id ? 'Cambiar a otro grupo' : 'Agrupar con...'}</label>
                            <AutocompleteGrupo
                              grupos={grupos.filter((g) => g.id !== p.grupo_id && !grupos.some((h) => h.padre_id === g.id))}
                              onSeleccionar={(g) => asignarGrupo(p, g.id)}
                              onCrear={(nombre) => crearYAsignarGrupo(p, nombre)}
                              placeholder="Buscar o crear grupo..."
                            />
                          </div>
                          {errorGrupo && <p className="text-red-600 text-xs">{errorGrupo}</p>}
                        </div>
                    </div>
        )}
      </>
    );
  }

  function renderCabeceraGrupo(entrada, { profundidad = 0 } = {}) {
    const abierto = gruposExpandidos.has(entrada.grupo_id);
    const subgrupos = entrada.hijosVisibles || [];
    const pad = 12 + profundidad * 24;
    const partesConteo = [];
    if (entrada.presentaciones.length) partesConteo.push(`${entrada.presentaciones.length} presentacion${entrada.presentaciones.length === 1 ? '' : 'es'}`);
    if (subgrupos.length) partesConteo.push(`${subgrupos.length} grupo${subgrupos.length === 1 ? '' : 's'}`);
    return (
      <div key={`grupo-${entrada.grupo_id}`} className={profundidad > 0 ? 'mt-2' : ''}>
        <div
          {...filaClickeable(() => alternarExpandirGrupo(entrada.grupo_id))}
          role="button"
          className={`flex items-center justify-between gap-2 rounded-md hover:bg-slate-50 transition-colors cursor-pointer py-1.5 pr-2 ${FOCO_FILA}`}
          style={{ paddingLeft: pad }}
        >
          <div className="flex items-center gap-1.5 font-medium text-sm whitespace-nowrap">
            {abierto ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
            {entrada.grupo_nombre}
            <span className="text-xs text-slate-400 font-normal">({partesConteo.join(', ') || 'vacío'})</span>
          </div>
          {!entrada.virtual && (
          <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => alternarCategoriaPanel(entrada.grupo_id, e)}
              title="Meter este grupo dentro de otro"
              className={`transition-colors ${categoriaAbiertaId === entrada.grupo_id ? 'text-indigo-600' : 'text-slate-400 hover:text-indigo-600'}`}
            >
              <Boxes size={14} />
            </button>
            <button
              onClick={(e) => abrirEdicionGrupo({ id: entrada.grupo_id, nombre: entrada.grupo_nombre }, e)}
              title="Editar"
              className="text-slate-400 hover:text-primary-600"
            >
              <Pencil size={14} />
            </button>
            <button
              onClick={(e) => eliminarGrupo({ id: entrada.grupo_id, nombre: entrada.grupo_nombre }, e)}
              title="Eliminar"
              className="text-slate-400 hover:text-red-600"
            >
              <Trash2 size={14} />
            </button>
          </div>
          )}
        </div>
        {grupoEnEdicionId === entrada.grupo_id && (
          <div style={{ paddingLeft: pad }}>{renderFormEdicionGrupo(entrada.grupo_id)}</div>
        )}
        {categoriaAbiertaId === entrada.grupo_id && (
          <div style={{ paddingLeft: pad }}>{renderPanelCategoriaGrupo(entrada.grupo_id)}</div>
        )}
        {abierto && (
          <div style={{ paddingLeft: pad + 20 }} className="mt-2 space-y-3">
            {subgrupos.map((sub) => renderCabeceraGrupo(sub, { profundidad: profundidad + 1 }))}
            {entrada.presentaciones.length > 0 && (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
                {entrada.presentaciones.map((p) => (
                  <React.Fragment key={p.id}>
                    {renderTarjetaProducto(p)}
                    {renderPanelesProducto(p)}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderPanelCategoriaGrupo(grupoId) {
    const grupoInfo = gruposPorId.get(grupoId);
    // No se puede meter un grupo dentro de sí mismo ni de un descendiente.
    const descendientes = new Set();
    (function marcar(id) {
      grupos.forEach((g) => {
        if (g.padre_id === id && !descendientes.has(g.id)) {
          descendientes.add(g.id);
          marcar(g.id);
        }
      });
    })(grupoId);
    const candidatos = grupos
      .filter((g) => g.id !== grupoId && !descendientes.has(g.id))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return (
      <div className="bg-slate-50 rounded-lg p-4 mt-1">
        <div className="space-y-3">
          {grupoInfo?.padre_id ? (
            <p className="text-sm text-slate-600">
              Dentro de <span className="font-medium">{grupoInfo.padre_nombre}</span>.{' '}
              <button onClick={() => quitarCategoria(grupoId)} className="text-red-600 hover:underline">Sacar de ese grupo</button>
            </p>
          ) : (
            <p className="text-sm text-slate-400">Este grupo no está dentro de ningún otro grupo todavía.</p>
          )}
          <div className="w-64">
            <label className="block text-xs text-slate-500 mb-1">{grupoInfo?.padre_id ? 'Mover a otro grupo' : 'Meter dentro de...'}</label>
            <AutocompleteGrupo
              grupos={candidatos}
              onSeleccionar={(g) => asignarCategoria(grupoId, g.id)}
              onCrear={(nombre) => crearYAsignarCategoria(grupoId, nombre)}
              placeholder="Buscar o crear grupo..."
            />
          </div>
          {errorCategoriaGrupo && <p className="text-red-600 text-xs">{errorCategoriaGrupo}</p>}
        </div>
      </div>
    );
  }

  function renderFormEdicionGrupo(grupoId) {
    return (
      <div className="bg-slate-50 rounded-lg p-4 mt-1">
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nombre</label>
              <input
                autoFocus
                value={formEditarGrupo.nombre}
                onChange={(e) => setFormEditarGrupo({ ...formEditarGrupo, nombre: e.target.value })}
                className="border rounded-md px-2 py-1.5 text-sm"
              />
            </div>
            <button onClick={() => guardarEdicionGrupo(grupoId)} className="bg-primary-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-primary-700">
              Guardar
            </button>
            <button onClick={() => setGrupoEnEdicionId(null)} className="text-slate-500 text-sm hover:underline">
              Cancelar
            </button>
          </div>
          {errorEditarGrupo && <p className="text-red-600 text-xs">{errorEditarGrupo}</p>}
        </div>
      </div>
    );
  }
}
